import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface Profile {
  id: string;
  full_name: string;
  role: "entrepreneur" | "mentor" | "admin";
  avatar_url: string | null;
  bio: string | null;
  pitch_deck_url: string | null;
  is_published: boolean;
  is_verified: boolean;
  is_onboarded: boolean;
  has_seen_tour: boolean;
  has_completed_suggested_connections: boolean;
  created_at: string;
  updated_at: string;
  // Private demographic fields (for EAB internal research only)
  referred_by: string | null;
  gender: string | null;
  age_range: string | null;
  has_device: string | null;
  education_level: string | null;
  // Capability flags (independent of primary role)
  is_mentor: boolean;
  is_entrepreneur: boolean;
}

export interface EntrepreneurProfile {
  id: string;
  company_name: string | null;
  company_description: string | null;
  startup_stage: "idea" | "prototype" | "mvp" | "seed" | "series_a" | "growth" | null;
  target_market: string | null;
  funding_status: string | null;
  specific_challenges: string | null;
}

export interface MentorProfile {
  id: string;
  professional_background: string | null;
  years_experience: number | null;
  career_highlights: string | null;
  availability_notes: string | null;
}

export function useProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data as Profile | null;
    },
    enabled: !!user?.id,
  });
}

export function useEntrepreneurProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["entrepreneur_profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("entrepreneur_profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      return data as EntrepreneurProfile | null;
    },
    enabled: !!user?.id,
  });
}

export function useMentorProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentor_profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("mentor_profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      return data as MentorProfile | null;
    },
    enabled: !!user?.id,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update profile: " + error.message);
    },
  });
}

export function useUpdateEntrepreneurProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<EntrepreneurProfile>) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const { data: existing } = await supabase
        .from("entrepreneur_profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("entrepreneur_profiles")
          .update(updates)
          .eq("id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("entrepreneur_profiles")
          .insert({ id: user.id, ...updates });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entrepreneur_profile"] });
      toast.success("Company profile updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update company profile: " + error.message);
    },
  });
}

export function useUpdateMentorProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<MentorProfile>) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const { data: existing } = await supabase
        .from("mentor_profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("mentor_profiles")
          .update(updates)
          .eq("id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("mentor_profiles")
          .insert({ id: user.id, ...updates });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentor_profile"] });
      toast.success("Professional profile updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update professional profile: " + error.message);
    },
  });
}

export function useUserSkills() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user_skills", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("user_skills")
        .select("skill_id, skills(id, name, category)")
        .eq("user_id", user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

export function useUserIndustries() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user_industries", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("user_industries")
        .select("industry_id, industries(id, name)")
        .eq("user_id", user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}
